import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, BarChart3, Landmark, UsersRound, WalletCards } from "lucide-react";
import { api } from "../api/client";
import { LoadingBlock, Section } from "../components/ui";
import { todayInJapan } from "../domain/form";
import { minutesLabel, yen } from "../../shared/calculations";
import type { MonthlyResult } from "../../shared/domain";

export function MonthlyPage() {
  const initialMonth = useMemo(() => todayInJapan().slice(0, 7), []);
  const [month, setMonth] = useState(initialMonth);
  const [data, setData] = useState<MonthlyResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    api.getMonthly(month).then(setData).catch((reason: Error) => setError(reason.message)).finally(() => setLoading(false));
  };

  useEffect(load, [month]);

  return (
    <div className="page-stack">
      <header className="page-heading page-heading--with-control">
        <div>
          <span className="eyebrow"><BarChart3 size={15} /> 月次集計</span>
          <h1>月の数字を、ひと目で</h1>
          <p>現在revisionの営業日だけを集計しています。後日入金は売上へ再加算しません。</p>
        </div>
        <label className="month-picker">
          <span>対象月</span>
          <input type="month" value={month} onChange={(event) => setMonth(event.target.value)} />
        </label>
      </header>

      {error ? <div className="inline-alert inline-alert--error" role="alert">{error}</div> : null}
      {loading ? <LoadingBlock label="月次集計を読み込んでいます" /> : data ? (
        <>
          {data.warnings.length ? (
            <div className="inline-alert inline-alert--warning"><AlertTriangle size={18} /> {data.warnings.join(" / ")}</div>
          ) : null}
          <div className="metric-hero-grid">
            <article className="metric-hero metric-hero--primary">
              <span>総売上</span><strong>{yen(data.totals.salesTotal)}</strong><small>現金・各種決済を含む</small>
            </article>
            <article className="metric-hero">
              <span>総利益</span><strong className={data.totals.dailyProfit < 0 ? "is-negative" : ""}>{yen(data.totals.dailyProfit)}</strong><small>売上 − 経費 − バイト代</small>
            </article>
            <article className="metric-hero">
              <span>総経費</span><strong>{yen(data.totals.expenseTotal)}</strong><small>当月の経費明細</small>
            </article>
            <article className="metric-hero">
              <span>バイト総額</span><strong>{yen(data.totals.wageTotal)}</strong><small>深夜割増を含む</small>
            </article>
          </div>

          <div className="two-column-grid">
            <Section title="売上の内訳" description="決済方法別" icon={<WalletCards size={19} />}>
              <dl className="breakdown-list">
                <div><dt>現金</dt><dd>{yen(data.totals.cashSales)}</dd></div>
                <div><dt>PayPay</dt><dd>{yen(data.totals.paypaySales)}</dd></div>
                <div><dt>クレジットカード</dt><dd>{yen(data.totals.cardSales)}</dd></div>
                <div><dt>ツケ発生</dt><dd>{yen(data.totals.tabSales)}</dd></div>
                <div><dt>振込予定発生</dt><dd>{yen(data.totals.transferDueSales)}</dd></div>
              </dl>
            </Section>
            <Section title="未回収・レジ" description="売上とは分けて管理" icon={<Landmark size={19} />}>
              <dl className="breakdown-list">
                <div><dt>未回収ツケ残高</dt><dd>{yen(data.totals.tabOutstanding)}</dd></div>
                <div><dt>未回収振込残高</dt><dd>{yen(data.totals.transferDueOutstanding)}</dd></div>
                <div className="breakdown-list__emphasis"><dt>レジ差額合計</dt><dd className={data.totals.registerDifference !== 0 ? "is-warning" : ""}>{yen(data.totals.registerDifference)}</dd></div>
              </dl>
            </Section>
          </div>

          <Section title="従業員別の勤務集計" description="通常・深夜時間と支給見込額" icon={<UsersRound size={19} />}>
            {data.staff.length === 0 ? <p className="muted">この月の勤務明細はありません。</p> : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead><tr><th>氏名</th><th>通常時間</th><th>深夜時間</th><th>総時間</th><th>基本賃金</th><th>深夜割増</th><th>合計</th></tr></thead>
                  <tbody>{data.staff.map((row) => (
                    <tr key={row.staffId}><th>{row.staffName}</th><td>{minutesLabel(row.normalMinutes)}</td><td>{minutesLabel(row.lateNightMinutes)}</td><td>{minutesLabel(row.totalMinutes)}</td><td>{yen(row.baseWage)}</td><td>{yen(row.lateNightPremium)}</td><td><strong>{yen(row.totalWage)}</strong></td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      ) : null}
    </div>
  );
}
